import update from "immutability-helper";
import { UpdateAction, UpdateActionTypes } from "./Actions";
import { IUpdateState, IFilter } from "./Models";

const initialState: IUpdateState = {
  failed: false,
  fetched: false,
  fetching: false,
  error: '',
  orderBy: 'docUpdateDays desc', // Default sorting by Doc Update(days) in descending order
  updates: {
    firstRowOnPage: 0,
    lastRowOnPage: 10,
    pageCount: 0,
    pageSize: 10,
    rowCount: 0,
    currentPage: 1,
    sortOrder: '',
    results: []
  },
  filters: [], // Array of filter objects like Analytics
  filteredResults: []
};

// Helper function to update filters array (same pattern as Analytics)
const updateFilters = (filters: IFilter[], field: string, value: any, operator: string = 'eq'): IFilter[] => {
  const updatedFilters = JSON.parse(JSON.stringify(filters));
  const index = filters.findIndex((obj: IFilter) => obj.field === field && obj.operator === operator);
  
  if (index > -1) {
    if (value !== undefined && value !== null && value !== '') {
      updatedFilters[index].value = value;
    } else {
      updatedFilters.splice(index, 1);
    }
  } else if (value !== undefined && value !== null && value !== '') {
    updatedFilters.push({ field, value, operator });
  }
  
  return updatedFilters;
};

// Helper function to remove all filters for a field
const removeFiltersForField = (filters: IFilter[], field: string): IFilter[] => {
  return filters.filter((obj: IFilter) => obj.field !== field);
};

export const UpdateReducer = (
  state = initialState,
  action: UpdateAction
): IUpdateState => {
  switch (action.type) {
    case UpdateActionTypes.FetchUpdatesPending:
      return update(state, {
        fetched: { $set: false },
        fetching: { $set: true },
        failed: { $set: false },
        error: { $set: '' }
      });
    case UpdateActionTypes.FetchUpdatesRejected:
      return update(state, {
        updates: { $set: { ...initialState.updates } },
        error: { $set: action.payload.error },
        fetched: { $set: false },
        fetching: { $set: false },
        failed: { $set: true }
      });
    case UpdateActionTypes.FetchedUpdates:
      return update(state, {
        fetched: { $set: true },
        fetching: { $set: false },
        failed: { $set: false },
        error: { $set: '' },
        updates: { $set: { ...action.payload.updates } },
        // Use the original results without filtering (server handles filtering)
        filteredResults: { $set: action.payload.updates.results || [] }
      });
    case UpdateActionTypes.SetSortOrder:
      return update(state, {
        orderBy: { $set: action.payload.orderBy }
      });
    case UpdateActionTypes.SetFilter: {
      // Handle filter value - could be simple value or object with from/to for ranges
      const { field, value, operator } = action.payload;
      let newFilters = [...state.filters];
      
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Handle range filters (from/to for dates, min/max for numbers)
        if ('from' in value || 'to' in value) {
          // Date range filter
          if (value.from) {
            newFilters = updateFilters(newFilters, field, value.from.value, value.from.operator);
          }
          if (value.to) {
            newFilters = updateFilters(newFilters, field, value.to.value, value.to.operator);
          }
        } else {
          // Simple object value - use operator from action payload or default to 'eq'
          newFilters = updateFilters(newFilters, field, value, operator || 'eq');
        }
      } else {
        // Simple value or array - use operator from action payload or default to 'eq'
        newFilters = updateFilters(newFilters, field, value, operator || 'eq');
      }
      
      return update(state, {
        filters: { $set: newFilters },
        filteredResults: { $set: state.updates.results || [] }
      });
    }
    case UpdateActionTypes.ClearFilter:
      return update(state, {
        filters: { $set: removeFiltersForField(state.filters, action.payload.field) },
        filteredResults: { $set: state.updates.results || [] }
      });
    case UpdateActionTypes.ClearAllFilters:
      return update(state, {
        filters: { $set: [] },
        filteredResults: { $set: state.updates.results || [] }
      });
    case UpdateActionTypes.ResetSortOrder:
      return update(state, {
        orderBy: { $set: initialState.orderBy }
      });
    default:
      return state;
  }
}
